// Canonical condition data — single source of truth.
// Add new conditions here and they auto-render at /conditions/:slug.
// Product slugs MUST match the exact slug stored in MongoDB (seeded in backend/server.py).

export const CONDITIONS = {
  "knee-osteoarthritis": {
    slug: "knee-osteoarthritis",
    name: "Knee Osteoarthritis",
    tag: "Joint & Orthopaedic",
    hero: "Supporting mobility, strength and active recovery.",
    understanding:
      "Knee osteoarthritis is a common condition affecting cartilage and joint function. It is typically managed through a combination of physiotherapy, mobility work, strengthening exercises and everyday nutritional support.",
    goals: ["Mobility", "Strength", "Function", "Activity tolerance", "Independence"],
    where_nutrition_fits:
      "Nutrition is complementary to your clinical management and physiotherapy programme. Joint & Mobility formulas provide targeted nutritional support alongside your rehabilitation plan.",
    primary_category: "Joint & Mobility",
    primary_product_slug: "physiomoveflex-joint-mobility-collagen",
    supporting_product_slug: "mag-forte-d3-magnesium-vitamin-d3",
    pathway: ["ASSESS", "REHABILITATE", "STRENGTHEN", "MOVE", "MAINTAIN"],
    seo_title: "Knee Osteoarthritis · Joint Nutritional Support | Physio Essential",
    seo_desc: "Nutritional support for knee osteoarthritis, designed to complement physiotherapy-led rehabilitation.",
  },
  "hip-osteoarthritis": {
    slug: "hip-osteoarthritis",
    name: "Hip Osteoarthritis",
    tag: "Joint & Orthopaedic",
    hero: "Nutritional support for hip mobility and active recovery.",
    understanding:
      "Hip osteoarthritis affects joint cartilage and mobility. Clinical management centres on physiotherapy, mobility, strength and everyday nutritional support tailored to the patient's stage of recovery.",
    goals: ["Hip mobility", "Strength", "Walking tolerance", "Function", "Independence"],
    where_nutrition_fits:
      "PhysiomoveFlex is designed to complement hip-focused physiotherapy and strengthening programmes with nutritional support for joint health.",
    primary_category: "Joint & Mobility",
    primary_product_slug: "physiomoveflex-joint-mobility-collagen",
    supporting_product_slug: "mag-forte-d3-magnesium-vitamin-d3",
    pathway: ["ASSESS", "REHABILITATE", "STRENGTHEN", "WALK", "MAINTAIN"],
    seo_title: "Hip Osteoarthritis · Nutritional Support | Physio Essential",
    seo_desc: "Nutritional support for hip osteoarthritis designed to complement physiotherapy programmes.",
  },
  "shoulder-rehabilitation": {
    slug: "shoulder-rehabilitation",
    name: "Shoulder Rehabilitation",
    tag: "Joint & Orthopaedic",
    hero: "Nutritional support alongside shoulder mobility and strengthening.",
    understanding:
      "Shoulder rehabilitation typically progresses through mobility, stability and strengthening phases guided by your physiotherapist. Nutritional support plays a complementary role.",
    goals: ["Range of motion", "Stability", "Strength", "Function"],
    where_nutrition_fits: "Formulas in the Joint & Mobility category may complement shoulder rehabilitation with targeted joint-nutrition support.",
    primary_category: "Joint & Mobility",
    primary_product_slug: "physiomoveflex-joint-mobility-collagen",
    supporting_product_slug: "mag-forte-d3-magnesium-vitamin-d3",
    pathway: ["ASSESS", "MOBILISE", "STABILISE", "STRENGTHEN", "MAINTAIN"],
    seo_title: "Shoulder Rehabilitation · Nutritional Support | Physio Essential",
    seo_desc: "Nutritional support that complements shoulder rehabilitation and physiotherapy programmes.",
  },
  "back-neck-rehabilitation": {
    slug: "back-neck-rehabilitation",
    name: "Back & Neck Rehabilitation",
    tag: "Spine",
    hero: "Nutritional support alongside spine-focused rehabilitation.",
    understanding:
      "Back and neck rehabilitation focuses on posture, mobility and strengthening under physiotherapy guidance. Nutrition provides supporting building blocks for everyday recovery.",
    goals: ["Mobility", "Posture", "Strength", "Endurance"],
    where_nutrition_fits: "Nutritional support may complement physiotherapy-led spine care, particularly during mobility and strengthening phases.",
    primary_category: "Joint & Mobility",
    primary_product_slug: "physiomoveflex-joint-mobility-collagen",
    supporting_product_slug: "mag-forte-d3-magnesium-vitamin-d3",
    pathway: ["ASSESS", "MOBILISE", "STRENGTHEN", "POSTURE", "MAINTAIN"],
    seo_title: "Back & Neck Rehabilitation Nutrition | Physio Essential",
    seo_desc: "Nutritional support alongside back and neck rehabilitation programmes.",
  },
  "acl-rehabilitation": {
    slug: "acl-rehabilitation",
    name: "ACL Rehabilitation",
    tag: "Sports Rehabilitation",
    hero: "Nutritional support that complements post-ACL rehabilitation.",
    understanding:
      "ACL rehabilitation progresses through defined phases led by your surgeon and physiotherapist, from early recovery through to return-to-activity. Nutrition is one supportive element within that broader clinical plan.",
    goals: ["Early recovery", "ROM", "Strengthening", "Return-to-sport"],
    where_nutrition_fits: "PhysiomoveFlex nutritional support may complement your prescribed rehabilitation programme. It does not accelerate surgical recovery or replace clinical care.",
    primary_category: "Joint & Mobility",
    primary_product_slug: "physiomoveflex-joint-mobility-collagen",
    supporting_product_slug: "physiofuel-am-pm-3-phase-recovery-system",
    pathway: ["POST-OP", "EARLY MOBILITY", "STRENGTHEN", "PROGRESS", "RETURN"],
    seo_title: "ACL Rehabilitation Nutrition | Physio Essential",
    seo_desc: "Nutritional support that complements ACL rehabilitation programmes.",
  },
  "fracture-recovery": {
    slug: "fracture-recovery",
    name: "Fracture Recovery",
    tag: "Bone",
    hero: "Bone-health nutrition alongside fracture rehabilitation.",
    understanding:
      "Fracture recovery is led by your treating clinician and involves immobilisation, gradual mobilisation and progressive strengthening. Adequate nutritional intake supports overall recovery.",
    goals: ["Bone recovery", "Mobility restoration", "Strength", "Return to function"],
    where_nutrition_fits: "Vitamin D and magnesium play recognised roles in bone and muscle function. GD-Nano-D3 and Mag Forte D3 provide nutritional support during rehabilitation.",
    primary_category: "Bone Health",
    primary_product_slug: "gd-nano-d3-vitamin-d3-60000-iu",
    supporting_product_slug: "mag-forte-d3-magnesium-vitamin-d3",
    pathway: ["IMMOBILISE", "MOBILISE", "STRENGTHEN", "REBUILD", "MAINTAIN"],
    seo_title: "Fracture Recovery Nutrition | Physio Essential",
    seo_desc: "Bone-health nutritional support alongside fracture-recovery rehabilitation.",
  },
  "geriatric-mobility": {
    slug: "geriatric-mobility",
    name: "Healthy Aging & Mobility",
    tag: "Geriatric Mobility",
    hero: "Nutritional support for mobility, bone and muscle function in later life.",
    understanding:
      "Healthy aging combines regular activity, strength work, mobility exercises and everyday nutrition. Physiotherapy-led programmes remain central to functional independence.",
    goals: ["Mobility", "Bone support", "Muscle function", "Balance", "Independence"],
    where_nutrition_fits: "Vitamin D, magnesium and joint nutritional formulas may complement mobility programmes for older adults.",
    primary_category: "Joint & Mobility",
    primary_product_slug: "physiomoveflex-joint-mobility-collagen",
    supporting_product_slug: "mag-forte-d3-magnesium-vitamin-d3",
    pathway: ["ASSESS", "MOBILISE", "STRENGTHEN", "MAINTAIN"],
    seo_title: "Healthy Aging & Mobility Nutrition | Physio Essential",
    seo_desc: "Nutritional support for mobility, bone and muscle function during healthy aging.",
  },
  "joint-stiffness": {
    slug: "joint-stiffness",
    name: "Joint Stiffness & Mobility",
    tag: "Joint Health",
    hero: "Supporting mobility, strength and active recovery.",
    understanding:
      "Joint stiffness is a common feature of ageing, active lifestyles and post-injury recovery. It is typically managed through a combination of physiotherapy, movement, strength work and everyday nutritional support.",
    goals: ["Mobility", "Strength", "Function", "Activity tolerance", "Independence"],
    where_nutrition_fits:
      "Nutrition is complementary to your clinical management and physiotherapy programme. Formulas in the Joint & Mobility category are designed to provide targeted nutritional support alongside your rehabilitation plan.",
    primary_category: "Joint & Mobility",
    primary_product_slug: "physiomoveflex-joint-mobility-collagen",
    supporting_product_slug: "mag-forte-d3-magnesium-vitamin-d3",
    pathway: ["ASSESS", "REHABILITATE", "STRENGTHEN", "MOVE", "RECOVER", "MAINTAIN"],
    seo_title: "Joint Stiffness & Mobility · Nutritional Support | Physio Essential",
    seo_desc: "Nutritional support for joint stiffness and mobility, designed to complement physiotherapy-led rehabilitation.",
  },
  "muscle-cramps": {
    slug: "muscle-cramps",
    name: "Muscle Cramps & Fatigue",
    tag: "Muscle Recovery",
    hero: "Supporting normal muscle function and everyday recovery.",
    understanding:
      "Muscle cramps and fatigue can affect active adults, athletes and rehabilitation patients. Physiotherapy focuses on loading, mobility and technique — with nutritional support playing a complementary role.",
    goals: ["Muscle function", "Recovery", "Training tolerance", "Sleep quality", "Activity resumption"],
    where_nutrition_fits:
      "Formulas in the Muscle Recovery category are designed to support normal muscle function alongside exercise, mobility and rehabilitation programmes.",
    primary_category: "Muscle Recovery",
    primary_product_slug: "myo-release-muscle-cramp-tension-formula",
    supporting_product_slug: "mag-forte-d3-magnesium-vitamin-d3",
    pathway: ["ASSESS", "LOAD", "STRENGTHEN", "RECOVER", "PROGRESS"],
    seo_title: "Muscle Cramps & Fatigue · Recovery Nutrition | Physio Essential",
    seo_desc: "Nutritional support for muscle cramps, fatigue and everyday recovery — designed to complement physiotherapy programmes.",
  },
  "bone-health": {
    slug: "bone-health",
    name: "Bone Health",
    tag: "Bone Support",
    hero: "Nutritional support for bones and muscle function.",
    understanding:
      "Bone health is influenced by many factors including nutrition, physical activity, age and clinical history. Vitamin D and magnesium play recognised roles in normal bone and muscle function.",
    goals: ["Bone support", "Muscle function", "Fall prevention", "Healthy aging", "Activity maintenance"],
    where_nutrition_fits:
      "Vitamin D and magnesium supplementation may complement bone-focused rehabilitation and everyday nutritional needs where diet alone may not be sufficient.",
    primary_category: "Bone Health",
    primary_product_slug: "gd-nano-d3-vitamin-d3-60000-iu",
    supporting_product_slug: "mag-forte-d3-magnesium-vitamin-d3",
    pathway: ["ASSESS", "SUPPORT", "STRENGTHEN", "MAINTAIN"],
    seo_title: "Bone Health · Vitamin D & Magnesium Support | Physio Essential",
    seo_desc: "Vitamin D and magnesium nutritional support for bone health, aging and muscle function.",
  },
  "b12-support": {
    slug: "b12-support",
    name: "B12 Nutritional Support",
    tag: "Nerve Health",
    hero: "Convenient vitamin B12 nutritional support.",
    understanding:
      "Vitamin B12 plays a role in normal nerve function and red blood cell formation. Certain diets and life stages can lead to inadequate B12 intake, and a healthcare professional may recommend supplementation.",
    goals: ["B12 nutritional support", "Nerve health", "Everyday wellbeing", "Diet-related supplementation"],
    where_nutrition_fits:
      "B12 Spray offers convenient supplementation for individuals whose intake may be inadequate. It does not treat neurological disease and is intended as nutritional support alongside professional clinical guidance.",
    primary_category: "Nerve Health",
    primary_product_slug: "vitamin-b12-spray-1500-mcg",
    supporting_product_slug: "mag-forte-d3-magnesium-vitamin-d3",
    pathway: ["ASSESS", "SUPPLEMENT", "MAINTAIN"],
    seo_title: "Vitamin B12 Nutritional Support | Physio Essential",
    seo_desc: "Convenient vitamin B12 nutritional support for individuals whose B12 intake may be inadequate.",
  },
  "sports-recovery": {
    slug: "sports-recovery",
    name: "Sports Recovery",
    tag: "Sports & Activity",
    hero: "Daily recovery nutrition for active lifestyles.",
    understanding:
      "Recovery is a cornerstone of every training programme. Balanced nutrition, adequate sleep and structured physiotherapy-led recovery all contribute to sustainable performance.",
    goals: ["Training recovery", "Return-to-activity", "Muscle function", "Active lifestyle", "Consistency"],
    where_nutrition_fits:
      "PhysioFuel AM-PM+ is designed to support daily training and recovery nutrition, complementing your training and physiotherapy plan.",
    primary_category: "Sports & Recovery Nutrition",
    primary_product_slug: "physiofuel-am-pm-3-phase-recovery-system",
    supporting_product_slug: "myo-release-muscle-cramp-tension-formula",
    pathway: ["TRAIN", "RECOVER", "REBUILD", "PROGRESS"],
    seo_title: "Sports Recovery Nutrition | Physio Essential",
    seo_desc: "Daily nutritional support designed for training recovery and active lifestyles.",
  },
  "joint-replacement-recovery": {
    slug: "joint-replacement-recovery",
    name: "Post-Surgery & Joint Replacement Recovery",
    tag: "Post-Op Rehab",
    hero: "Nutritional support that complements post-operative rehabilitation.",
    understanding:
      "Joint replacement and ACL rehabilitation are led by your surgeon and physiotherapist across defined phases: early recovery, functional recovery and long-term function. Nutrition is one supportive element within that broader clinical plan.",
    goals: ["Early recovery", "ROM restoration", "Strengthening", "Return to function", "Independence"],
    where_nutrition_fits:
      "PhysiomoveFlex offers joint and mobility nutritional support that may complement your prescribed rehabilitation programme. It does not accelerate surgical recovery or replace clinical care.",
    primary_category: "Joint & Mobility",
    primary_product_slug: "physiomoveflex-joint-mobility-collagen",
    supporting_product_slug: "mag-forte-d3-magnesium-vitamin-d3",
    pathway: ["POST-OP", "EARLY MOBILITY", "STRENGTHEN", "PROGRESS", "MAINTAIN"],
    seo_title: "Post-Surgery Joint Replacement Recovery | Physio Essential",
    seo_desc: "Nutritional support that complements post-operative rehabilitation across the joint replacement recovery journey.",
  },
};

export const CONDITION_SLUGS = Object.keys(CONDITIONS);
